import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  AlertDropDownOptions,
  Entity,
  KeyValue,
  ParameterOption,
  TradeCaptureAlert,
  TradeCaptureAlerts
} from '@approot/models';
import {
  AdminService,
  LegService,
  TradesCaptureService
} from '@approot/services';
import { DateUtilities } from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { cloneDeep } from 'lodash';

import dropDownOptions from './alert-dropdown-options.json';

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss']
})
export class AlertsComponent implements OnInit {
  @ViewChild('divToScroll', { static: false }) public divToScroll: ElementRef;
  @Input() public legComponents: QueryList<LegComponent>;
  public dropdownOptions: AlertDropDownOptions;
  public selectedAlertIndex: number = null;
  public alertsForm: FormGroup;
  public loadingAlerts = true;
  public showParameterModal = false;
  public fixingOutcomeSelected = false;
  public parametersFormGroup: FormGroup;
  public parametersToView = [];
  public alertTypeOptions: string[] = [];
  public statusTypeOptions: string[] = [];
  public entities: string[] = [];
  public dates: string[] = [];

  constructor(
    private tradeCaptureService: TradesCaptureService,
    private adminService: AdminService,
    private legService: LegService
  ) {
    this.dropdownOptions = dropDownOptions;
    // This type "mothballed, but might be useful in the future"
    this.dropdownOptions.alertTypes = this.dropdownOptions.alertTypes.filter(
      alertType => alertType.shortName !== 'fixingOutcome'
    );
  }

  public ngOnInit(): void {
    this.alertsForm = new FormGroup({
      alertsArray: new FormArray([])
    });

    this.alertTypeOptions = this.dropdownOptions.alertTypes.map(
      alert => alert.longName
    ); // Sets Alert name dropdowns

    this.statusTypeOptions = this.dropdownOptions.status.map(alertStatus =>
      alertStatus.valueOf()
    ); // Sets Status dropdowns

    this.tradeCaptureService
      .getUserInfoRequest({ task: 'getAlerts' })
      .subscribe({
        next: (response: TradeCaptureAlerts) => {
          response.alerts.forEach((alert: TradeCaptureAlert) => {
            // Run through loaded alerts and create form controls
            const group = this.formGroupCreator(alert);
            (this.alertsForm.get('alertsArray') as FormArray).push(group);
          });
          this.loadingAlerts = false;
        },
        error: () => {
          this.loadingAlerts = false;
        }
      });

    this.tradeCaptureService.getEntities().subscribe((entities: Entity[]) => {
      this.updateLocalEntities(entities); // since we are downloading the entities update in on the app.
    });
  }

  public alertTypeChange(alertForm: FormGroup) {
    // When user changes the Alert Type, the Parameter should change too
    alertForm.removeControl('parameters');
    alertForm.addControl(
      'parameters',
      this.parametersSetter({
        alertType: alertForm.get('alertType').value
      } as TradeCaptureAlert)
    );
  }

  private formGroupCreator(alert: TradeCaptureAlert) {
    // Deals with creating the form controls for the Alert
    const group = new FormGroup({});

    group.addControl('parameters', this.parametersSetter(alert));

    group.addControl(
      'alertType',
      new FormControl(alert && alert.alertType ? alert.alertType : null, [
        Validators.required
      ])
    );

    group.addControl(
      'identifier',
      new FormControl(alert && alert.identifier ? alert.identifier : '', [
        Validators.required
        //Identifier Validator
      ])
    );

    group.addControl(
      'status',
      new FormControl(alert && alert.status ? alert.status : null, [
        Validators.required
      ])
    );

    return group;
  }

  private parametersSetter(alert: TradeCaptureAlert) {
    // Deals with creating the form controls for the parameters
    let parametersGroup = new FormGroup({});

    if (alert) {
      const alertType = this.dropdownOptions.alertTypes.find(
        alertTypes => alertTypes.longName === alert.alertType
      );

      if (alertType) {
        alertType.parameters.forEach(parameter => {
          switch (parameter.shortName) {
            case 'entity':
            case 'currencyPair':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'barrier':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'validToDate':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? DateUtilities.getDateInDashedFormat(
                        alert[parameter.shortName]
                      )
                    : null,
                  [this.fromDateValidator]
                )
              );
              break;
            case 'direction':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'validToTime':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'timezone':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'code':
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
            case 'dates': // Control to add date to list of dates
              parametersGroup.addControl(
                parameter.shortName,
                new FormControl(
                  alert && alert[parameter.shortName]
                    ? alert[parameter.shortName]
                    : null,
                  [Validators.required]
                )
              );
              break;
          }
        });
      }
    }

    return parametersGroup;
  }

  public openParameters(alertForm: FormGroup) {
    this.parametersFormGroup = alertForm.get('parameters') as FormGroup;
    if (alertForm.get('alertType').value) {
      this.parametersToView = cloneDeep(
        this.dropdownOptions.alertTypes.find(
          alertType => alertType.longName === alertForm.get('alertType').value
        ).parameters // Gets parameters of an Alert based on the Long Name of the Alert
      );

      this.parametersToView.forEach((par: ParameterOption) => {
        switch (par.shortName) {
          case 'entity':
            par.options = this.entities;
            break;
        }
      });
    } else {
      this.parametersToView = [];
    }

    if (alertForm.get('alertType').value === 'fixingOutcome') {
      this.fixingOutcomeSelected = true;
    } else {
      this.fixingOutcomeSelected = false;
    }
    this.showParameterModal = true;
  }

  public addNewAlert() {
    // Add New Alert to the table
    (this.alertsForm.get('alertsArray') as FormArray).push(
      this.formGroupCreator(null)
    );
    this.updateScroll();
  }

  get alertsFormArray() {
    return (this.alertsForm.get('alertsArray') as FormArray)
      .controls as FormGroup[];
  }

  get alertsArray() {
    return this.alertsForm.get('alertsArray') as FormArray;
  }

  private updateScroll() {
    setTimeout(() => {
      let e = this.divToScroll.nativeElement;
      e.scrollTop = e.scrollHeight;
    });
  }

  fromDateValidator(control: AbstractControl): KeyValue | null {
    // field is valid if field is of the correct format or lag is present
    if (DateUtilities.getDateInDashedFormat(control.value)) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  parametersModalStatusChanged(event: boolean) {
    this.showParameterModal = event;
    // console.log('showParamStatus', event);
  }

  alertsPayloadFormatter(row) {
    // format the data into what the database is expecting
    const parameters = row.parameters;
    const status = row.status;
    const identifier = row.identifier;
    const alertType = row.alertType;

    if (parameters.validToDate) {
      parameters.validToDate = DateUtilities.getDateInDashedFormat(
        parameters.validToDate
      );
    }

    if (parameters.dates) {
      parameters.dates = DateUtilities.prettyDateToDate(parameters.dates);
    }

    let alertToPush = {
      alertType,
      identifier,
      ...parameters,
      status
    };

    if (
      alertToPush.validToDate !== undefined &&
      alertToPush.dates !== undefined
    ) {
      // only one needs to be saved
      if (alertToPush.validToDate) {
        delete alertToPush.dates;
      } else {
        delete alertToPush.validToDate;
      }
    }
    return alertToPush;
  }

  deleteAlert() {
    const alertToDelete = (this.alertsForm.get('alertsArray') as FormArray)
      .controls[this.selectedAlertIndex] as FormGroup;

    (this.alertsForm.get('alertsArray') as FormArray).removeAt(
      this.selectedAlertIndex
    );

    this.alertsArray.markAsDirty();
    this.selectedAlertIndex = null;
  }

  public saveChanges() {
    const alertRows = this.alertsArray.value as any[];

    let alerts = []; // alerts will be stored here

    alertRows.forEach((row: any) => {
      const alertsToPush = this.alertsPayloadFormatter(row);
      alerts.push(alertsToPush);
    });

    this.tradeCaptureService
      .getUserInfoRequest({
        task: 'setAlerts',
        item: {
          email: this.adminService.userInfo.email,
          alerts: alerts
        }
      })
      .subscribe(() => {
        this.alertsForm.markAsPristine();
      });
  }

  identifierValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    // validator makes sure that the identifier's are unique
    if (control.parent && control.parent.parent) {
      const previousValue = control.parent.value.identifier;
      const alertsArray = control.parent.parent.controls as FormGroup[];

      let duplicateIdentifiers = 0;

      // find the number of current duplicates and if the value has changed
      // make the duplicates update their validity to see if they are valid now.
      alertsArray.forEach(alert => {
        if (
          control.value !== previousValue &&
          alert.get('identifier').value === previousValue
        ) {
          alert.get('identifier').updateValueAndValidity(); // update the validity of other identifiers that had this
          // controls previous value
        }
        if (alert.get('identifier').value === control.value) {
          duplicateIdentifiers++; // duplicate found
        }
      }); // find all id's
      if (duplicateIdentifiers > 1) {
        // if there are 2+ alerts that have the same id
        return { invalidEntry: { value: control.value } };
      }
    }
    return null;
  }

  get allowedToSave(): boolean {
    return (
      (this.alertsArray.valid && this.alertsArray.dirty) ||
      (this.alertsArray.length === 0 && this.alertsArray.dirty)
    );
  }

  get showAlerts() {
    return this.tradeCaptureService.showAlerts;
  }

  modalChanged(event: boolean) {
    this.tradeCaptureService.setShowAlerts = event;
  }

  private updateLocalEntities(entities: Entity[]) {
    this.entities = this.legService.updateEntities(entities);
  }
}
